/*
 * Books' rules (draft 10, FEATURE-MAP 10), ported from the WordPress Steward's ledger.php: the standard categories with
 * their Schedule C lines, the 1099-NEC line by year, who a bank line says was paid, and which rule fits a description.
 */

/** income and expense count in profit; asset is bought to keep (not a cost this year); other is not profit at all. */
export const CATEGORY_TYPES = ["income", "expense", "asset", "other"] as const;
export type CategoryType = (typeof CATEGORY_TYPES)[number];

/** Where a category sits in the profit and loss. Labor and Job costs are direct costs: gross profit is income less them. */
export const CATEGORY_GROUPS: Record<CategoryType, readonly string[]> = {
  income: ["Income"],
  expense: ["Labor", "Job costs", "Vehicles", "Marketing", "Overhead"],
  asset: ["Bought to keep"],
  other: ["Not profit"],
};
export const DIRECT_GROUPS: readonly string[] = ["Labor", "Job costs"];

export interface StandardCategory {
  code: string;
  name: string;
  type: CategoryType;
  group: string;
  /** The Schedule C line, for the accountant. */
  taxLine: string | null;
}

const c = (code: string, name: string, type: CategoryType, group: string, taxLine: string | null = null): StandardCategory => ({
  code,
  name,
  type,
  group,
  taxLine,
});

/**
 * Every company starts with these. Tools and equipment under $2,500 each are deducted the year they are bought (the IRS
 * de minimis safe harbor), so they sit with job costs. A vehicle or equipment kept for years is bought to keep: not a
 * cost in the year's profit; the accountant deducts it through depreciation or Section 179 (Form 4562, line 13).
 */
export const STANDARD_CATEGORIES: readonly StandardCategory[] = [
  c("sales", "Sales", "income", "Income", "1"),
  c("otherinc", "Other income", "income", "Income", "6"),
  c("refunds", "Refunds to clients", "income", "Income", "2"),
  c("wages", "Wages (W-2)", "expense", "Labor", "26"),
  c("contract", "Contract labor (1099)", "expense", "Labor", "11"),
  c("paytax", "Payroll taxes", "expense", "Labor", "23"),
  c("supplies", "Job supplies", "expense", "Job costs", "22"),
  c("equipment", "Tools and equipment under $2,500 each", "expense", "Job costs", "22"),
  c("repairs", "Equipment repairs", "expense", "Job costs", "21"),
  c("fuel", "Gas", "expense", "Vehicles", "9"),
  c("vehicle", "Vehicle repairs and costs", "expense", "Vehicles", "9"),
  c("ads", "Advertising", "expense", "Marketing", "8"),
  c("software", "Software and apps", "expense", "Overhead", "18"),
  c("office", "Office", "expense", "Overhead", "18"),
  c("phone", "Phone and internet", "expense", "Overhead", "25"),
  c("utilities", "Utilities", "expense", "Overhead", "25"),
  c("insurance", "Insurance", "expense", "Overhead", "15"),
  c("fees", "Card and bank fees", "expense", "Overhead", "27a"),
  c("legal", "Legal and accounting", "expense", "Overhead", "17"),
  c("rent", "Rent and storage", "expense", "Overhead", "20b"),
  c("licenses", "Taxes and licenses", "expense", "Overhead", "23"),
  c("interest", "Loan interest", "expense", "Overhead", "16b"),
  c("meals", "Meals", "expense", "Overhead", "24b"),
  c("travel", "Travel", "expense", "Overhead", "24a"),
  c("uniforms", "Uniforms", "expense", "Overhead", "27a"),
  c("otherexp", "Other expenses", "expense", "Overhead", "27a"),
  c("vehiclebuy", "Vehicles bought", "asset", "Bought to keep", "13"),
  c("assetbuy", "Equipment bought to keep", "asset", "Bought to keep", "13"),
  c("transfer", "Transfer between our accounts", "other", "Not profit"),
  c("cardpay", "Credit card payment", "other", "Not profit"),
  c("draw", "Owner's draw", "other", "Not profit"),
  c("putin", "Owner put money in", "other", "Not profit"),
  c("loanin", "Loan received", "other", "Not profit"),
  c("loanpay", "Loan payment (principal)", "other", "Not profit"),
];

/**
 * The amount where a 1099-NEC is owed, by the year paid: $600 through 2025, $2,000 for payments after December 31, 2025
 * (One Big Beautiful Bill Act sec. 70433), indexed for inflation from 2027. Add a year here when the IRS sets it.
 */
const LINE_1099: Record<number, number> = { 2025: 600_00, 2026: 2000_00 };
export function line1099Cents(year: number): number {
  if (year <= 2025) return 600_00;
  const have = Object.keys(LINE_1099)
    .map(Number)
    .filter((y) => y <= year);
  return LINE_1099[Math.max(...have)]!;
}

/**
 * The parts of a bank's ACH line ("ORIG CO NAME:Square Inc ORIG ID:942... CO ENTRY DESCR:SQ260120 SEC:CCD ..."): the
 * company that sent or took the money and what it called the payment. null when the line is not one.
 */
export function achParts(description: string): { name: string; entry: string } | null {
  const d = description.replace(/\s+/g, " ");
  const name = /ORIG CO NAME:\s*(.+?)\s+ORIG ID:/i.exec(d)?.[1]?.trim();
  if (!name) return null;
  return { name, entry: /CO ENTRY DESCR:\s*(.+?)\s+SEC:/i.exec(d)?.[1]?.trim() ?? "" };
}

/** Short words for a list: the company on an ACH line, else the bank's words as they are. */
export const bankLabel = (description: string): string => achParts(description)?.name ?? description;

/**
 * Who a bank line says was paid or paid us: "Zelle payment to Kenia R JPM99..." is Kenia R, and an ACH line is its
 * company (Square Inc). Empty when it does not say.
 */
export function payeeGuess(description: string): string {
  const d = description.replace(/\s+/g, " ").trim();
  const m = /(?:zelle|venmo|cash ?app|paypal)\b.*?\bto\s+(.+)$/i.exec(d) ?? /\bzelle\s+(?:payment\s+)?to\s+(.+)$/i.exec(d);
  if (!m?.[1]) return achParts(d)?.name ?? "";
  const first = m[1].split(/\s+(?:(?:jpm|ref|conf|trn)\w*|(?:confirmation|id|on|memo|for)\b)|\s+[#*]?\d/i)[0] ?? "";
  const words = first
    .replace(/[^A-Za-z'\- ]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .slice(0, 3);
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

/** The same payee however the bank spells the spaces and dots: lower case letters, digits and single spaces. */
export function payeeKey(name: string): string {
  return name
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]+/gi, "")
    .trim()
    .toLowerCase();
}

/**
 * The first rule whose words are in the description, or null. Rules are kept newest first. A rule may also ask for money
 * in or money out only, or one account only (like QuickBooks' bank rules); with no amount or account given those parts
 * are not checked.
 */
export function ruleFor<R extends { match: string; direction?: string | null; accountId?: string | null }>(
  rules: readonly R[],
  description: string,
  on: { amountCents?: number; accountId?: string } = {},
): R | null {
  const d = description.toLowerCase();
  return (
    rules.find((r) => {
      if (r.match === "" || !d.includes(r.match.toLowerCase())) return false;
      if (on.amountCents !== undefined && r.direction === "in" && on.amountCents <= 0) return false;
      if (on.amountCents !== undefined && r.direction === "out" && on.amountCents >= 0) return false;
      if (on.accountId !== undefined && r.accountId && r.accountId !== on.accountId) return false;
      return true;
    }) ?? null
  );
}

/** The span a report covers, from a name the owner picks. today is "YYYY-MM-DD" in the company's time zone. */
export const REPORT_SPANS = ["month", "lastmonth", "quarter", "lastquarter", "year", "lastyear"] as const;
export type ReportSpan = (typeof REPORT_SPANS)[number];
export function spanOf(span: ReportSpan, today: string): { from: string; to: string } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  const ym = (yy: number, mm: number) => `${String(yy)}-${String(mm).padStart(2, "0")}`;
  const back = (yy: number, mm: number, n: number): [number, number] => {
    const i = yy * 12 + (mm - 1) - n;
    return [Math.floor(i / 12), (i % 12) + 1];
  };
  switch (span) {
    case "month":
      return { from: `${ym(y, m)}-01`, to: monthEnd(ym(y, m)) };
    case "lastmonth": {
      const [yy, mm] = back(y, m, 1);
      return { from: `${ym(yy, mm)}-01`, to: monthEnd(ym(yy, mm)) };
    }
    case "quarter":
    case "lastquarter": {
      const q0 = Math.floor((m - 1) / 3) * 3 + 1;
      const [yy, mm] = span === "quarter" ? [y, q0] : back(y, q0, 3);
      const [ey, em] = back(yy, mm, -2);
      return { from: `${ym(yy, mm)}-01`, to: monthEnd(ym(ey, em)) };
    }
    case "year":
      return { from: `${String(y)}-01-01`, to: `${String(y)}-12-31` };
    case "lastyear":
      return { from: `${String(y - 1)}-01-01`, to: `${String(y - 1)}-12-31` };
  }
}

/** The same span one year earlier, for "compare to last year". A month's last day stays its last day (Feb 28 or 29). */
export function yearBefore(span: { from: string; to: string }): { from: string; to: string } {
  const back = (d: string) => {
    const month = `${String(Number(d.slice(0, 4)) - 1)}-${d.slice(5, 7)}`;
    const end = monthEnd(month);
    return d === monthEnd(d.slice(0, 7)) || `${month}-${d.slice(8)}` > end ? end : `${month}-${d.slice(8)}`;
  };
  return { from: back(span.from), to: back(span.to) };
}

/** The last day of a month, "2026-02" to "2026-02-28". */
export function monthEnd(month: string): string {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
}
